import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth-context';
import { useNavigate } from 'react-router-dom';
import { getDocumentProcessingResults } from '../lib/api-client';
import { API_URL } from '../lib/config';
import ResultsModal from './ResultsModal';
import AlertDialog from './ui/AlertDialog';
import { Document, ProcessingStatus } from '../types/Document';

// Import refactored components
import DashboardKPICards from './dashboard/DashboardKPICards';
import DocumentTable from './dashboard/DocumentTable';

// Import custom hooks
import { useDocumentPolling } from '../hooks/useDocumentPolling';
import { useDocumentOperations } from '../hooks/useDocumentOperations';
import { useAlertDialog } from '../hooks/useAlertDialog';

export default function Dashboard() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processingStatuses, setProcessingStatuses] = useState<Record<string, ProcessingStatus>>({});
  const [processingResults, setProcessingResults] = useState<Record<string, any>>({});
  const [processingJobIds, setProcessingJobIds] = useState<Record<string, string>>({});
  const [docTypeSelections, setDocTypeSelections] = useState<Record<string, string>>({});
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const [selectedDocName, setSelectedDocName] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();
  const { alertState, closeAlert, confirmDelete } = useAlertDialog();

  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Callbacks for polling hook
  const handleStatusUpdate = useCallback((documentId: string, status: ProcessingStatus) => {
    setProcessingStatuses(prev => ({ ...prev, [documentId]: status }));
  }, []);

  const handleResultUpdate = useCallback((documentId: string, result: any) => {
    setProcessingResults(prev => ({ ...prev, [documentId]: result }));
  }, []);

  const handleAuthError = useCallback(() => {
    setTimeout(() => navigate('/login'), 2000);
  }, [navigate]);

  // Initialize polling hook
  const {
    startPolling,
    stopAllPolling,
    reconcileProcessingStatus
  } = useDocumentPolling({
    onStatusUpdate: handleStatusUpdate,
    onResultUpdate: handleResultUpdate,
    onError: (msg) => operationsError !== msg && setOperationsState(prev => ({ ...prev, error: msg })),
    onAuthError: handleAuthError,
    onSuccess: (msg) => setOperationsState(prev => ({ ...prev, message: msg }))
  });

  // State for operations hook messages
  const [operationsState, setOperationsState] = useState({ error: '', message: '' });
  const operationsError = operationsState.error;
  const operationsMessage = operationsState.message;

  const handleJobIdUpdate = useCallback((documentId: string, jobId: string) => {
    setProcessingJobIds(prev => ({ ...prev, [documentId]: jobId }));
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      if (!user || !user.token) {
        throw new Error('User not authenticated');
      }
      
      const response = await fetch(`${API_URL}/documents`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const data = await response.json();
      setDocuments(data);
      
      // Initialize processing statuses and document type selections
      const statusMap: Record<string, ProcessingStatus> = {};
      const resultMap: Record<string, any> = {};
      const jobIdMap: Record<string, string> = {};
      const typeSelectionsMap: Record<string, string> = {};

      data.forEach((doc: Document) => {
        statusMap[doc.id] = doc.processingStatus || 'not_processed';
        
        if (doc.processingResult) {
          resultMap[doc.id] = doc.processingResult;
        }
        
        if (doc.processingJobId) {
          jobIdMap[doc.id] = doc.processingJobId;
        }
        
        typeSelectionsMap[doc.id] = doc.documentType || 'procedure';
      });
      
      setProcessingStatuses(statusMap);
      setProcessingResults(resultMap);
      setProcessingJobIds(jobIdMap);
      setDocTypeSelections(prev => ({ ...prev, ...typeSelectionsMap }));
    } catch (err) {
      setOperationsState(prev => ({ 
        ...prev, 
        error: err instanceof Error ? err.message : 'Error fetching documents' 
      }));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initialize document operations hook
  const {
    processingDocId,
    error: opsError,
    message: opsMessage,
    handleFileUpload,
    handleDownload,
    handleDelete,
    handleProcess,
    handleDocTypeChange
  } = useDocumentOperations({
    token: user?.token,
    onDocumentsChange: fetchDocuments,
    onStatusUpdate: handleStatusUpdate,
    onJobIdUpdate: handleJobIdUpdate,
    startPolling,
    docTypeSelections,
    onAuthError: handleAuthError
  });

  // Merge errors and messages from both sources
  const error = opsError || operationsError;
  const message = opsMessage || operationsMessage;

  // Clear messages after timeout
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setOperationsState(prev => ({ ...prev, message: '' }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setOperationsState(prev => ({ ...prev, error: '' }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Initial fetch
  useEffect(() => {
    fetchDocuments();
    
    return () => {
      stopAllPolling();
    };
  }, [fetchDocuments, stopAllPolling]);

  // Reconcile processing statuses after documents are loaded
  useEffect(() => {
    if (documents.length > 0) {
      documents.forEach(doc => {
        const jobId = processingJobIds[doc.id] || doc.processingJobId;
        const status = processingStatuses[doc.id] || doc.processingStatus || 'not_processed';
        const result = processingResults[doc.id];
        
        if (jobId) {
          reconcileProcessingStatus(doc.id, jobId, status, result);
        }
      });
    }
  }, [documents, processingJobIds, processingStatuses, processingResults, reconcileProcessingStatus]);

  const handleDocumentClick = useCallback((documentId: string) => {
    navigate(`/documents/${documentId}`);
  }, [navigate]);

  const handleTypeChange = useCallback(async (documentId: string, typeId: string) => {
    const previousType = docTypeSelections[documentId];
    setDocTypeSelections(prev => ({ ...prev, [documentId]: typeId }));
    
    try {
      await handleDocTypeChange(documentId, typeId);
    } catch {
      // Revert on error
      setDocTypeSelections(prev => ({ ...prev, [documentId]: previousType || 'procedure' }));
    }
  }, [docTypeSelections, handleDocTypeChange]);

  const handleProcessDocument = useCallback((documentId: string) => {
      const doc = documents.find(d => d.id === documentId);
    if (doc) {
      handleProcess(doc);
    }
  }, [documents, handleProcess]);

  const handleDeleteWithConfirmation = useCallback((documentId: string) => {
    const doc = documents.find(d => d.id === documentId);
    const docName = doc?.originalName || 'this document';
    confirmDelete(docName, () => handleDelete(documentId));
  }, [documents, confirmDelete, handleDelete]);

  const viewProcessingResults = useCallback(async (documentId: string) => {
    try {
      if (!user || !user.token) {
        throw new Error('User not authenticated');
      }
      
      const doc = documents.find(d => d.id === documentId);
      if (!doc) {
        throw new Error('Document not found');
      }
      
        const resultsResponse = await getDocumentProcessingResults(documentId);
        
        if (resultsResponse.processingResult) {
          setProcessingResults(prev => ({ 
            ...prev, 
            [documentId]: resultsResponse.processingResult 
          }));
        }
        
        setSelectedResult(resultsResponse.processingResult);
        setSelectedDocName(doc.originalName);
        setModalOpen(true);
    } catch (err) {
      if (err instanceof Error && err.message.includes('authentication')) {
        setOperationsState(prev => ({ ...prev, error: 'Authentication error: Please log in again' }));
        handleAuthError();
        return;
      }
      setOperationsState(prev => ({ 
        ...prev,
        error: err instanceof Error ? err.message : 'Error viewing processing results' 
      }));
    }
  }, [user, documents, handleAuthError]);

  const onUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    
    setUploading(true);
    await handleFileUpload(files);
    setUploading(false);
  }, [handleFileUpload]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 mt-16">
      {/* Success message */}
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span className="block sm:inline">{message}</span>
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPI Cards */}
        <DashboardKPICards 
          documents={documents} 
          processingStatuses={processingStatuses} 
        />

        {/* Documents Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-semibold text-gray-900">Documents</h1>
              <div className="relative">
                <input
                  type="file"
                  onChange={onUpload}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.docx"
                  disabled={uploading}
                  multiple
                />
                <label
                  htmlFor="file-upload"
                  className={`inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 cursor-pointer ${
                    uploading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                      </svg>
                      Upload Document
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>

          <DocumentTable
            documents={documents}
            processingStatuses={processingStatuses}
            processingResults={processingResults}
            processingDocId={processingDocId}
            docTypeSelections={docTypeSelections}
            onDocumentClick={handleDocumentClick}
            onTypeChange={handleTypeChange}
            onProcess={handleProcessDocument}
            onDownload={handleDownload}
            onDelete={handleDeleteWithConfirmation}
            onViewResults={viewProcessingResults}
          />
        </div>
      </div>

      {/* Results Modal */}
      <ResultsModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        results={selectedResult}
        documentName={selectedDocName}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={alertState.isOpen}
        onClose={closeAlert}
        onConfirm={alertState.onConfirm}
        title={alertState.title}
        message={alertState.message}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        variant={alertState.variant}
        confirmButtonVariant={alertState.confirmButtonVariant}
      />
    </div>
  );
} 
